import { useQuery, useQueryClient } from '@tanstack/react-query'
import type React from 'react'
import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './AuthProvider'

type Company = {
  id: string
  name: string
}

type CompanyContextType = {
  activeCompanyId: string | null
  setActiveCompanyId: (id: string) => Promise<void>
  companies: Company[]
  isLoading: boolean
  isSwitching: boolean
}

const CompanyContext = createContext<CompanyContextType>({
  activeCompanyId: null,
  setActiveCompanyId: async () => {},
  companies: [],
  isLoading: true,
  isSwitching: false,
})

export function CompanyProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [activeCompanyId, setActiveCompanyIdState] = useState<string | null>(null)
  const [isSwitching, setIsSwitching] = useState(false)

  const { data: companies = [], isLoading } = useQuery({
    queryKey: ['user-companies', user?.id],
    queryFn: async () => {
      if (!user) return []

      const { data, error } = await supabase.from('company_settings').select('id, name, custom_features')

      if (error) {
        console.error('Erro ao buscar empresas:', error)
        return []
      }

      return data as Company[]
    },
    enabled: !!user,
  })

  useEffect(() => {
    if (user) {
      // Sempre prioriza o que vem do JWT no servidor para evitar bugs de RLS
      const currentCompanyFromJwt = user.app_metadata?.company_id as string | undefined
      const saved = localStorage.getItem(`activeCompanyId_${user.id}`)

      if (currentCompanyFromJwt) {
         setActiveCompanyIdState(currentCompanyFromJwt)
         localStorage.setItem(`activeCompanyId_${user.id}`, currentCompanyFromJwt)
      } else if (saved) {
         setActiveCompanyIdState(saved)
      }
    } else {
      setActiveCompanyIdState(null)
    }
  }, [user])

  const setActiveCompanyId = async (id: string) => {
    if (id === activeCompanyId || !user) return

    setIsSwitching(true)
    try {
      // 1. Atualiza no backend (RPC atualiza o app_metadata)
      const { error } = await supabase.rpc('switch_active_company', { p_company_id: id })
      
      // Se a funo ainda no existir no banco (migrao pendente), avisa no console e faz s local
      if (error && error.message?.includes('function switch_active_company does not exist')) {
         console.warn('Backend migration pendente. O switch de contexto funcionar apenas localmente.')
         setActiveCompanyIdState(id)
         localStorage.setItem(`activeCompanyId_${user.id}`, id)
         return
      } else if (error) {
         throw error
      }

      // 2. Fora o Supabase a renovar o Token JWT localmente com a nova role/company_id
      const { error: refreshError } = await supabase.auth.refreshSession()
      if (refreshError) throw refreshError

      // 3. Atualiza estado local e persistncia
      setActiveCompanyIdState(id)
      localStorage.setItem(`activeCompanyId_${user.id}`, id)
      
      // 4. Invalida todas as queries do React Query para forar recarregamento de tudo com o novo contexto
      queryClient.clear() 
      
    } catch (err) {
      console.error('Erro ao trocar de filial:', err)
      // Removemos o alert para no travar a renderizao em caso de loop
      // alert('Erro ao trocar de filial. Tente novamente.')
    } finally {
      setIsSwitching(false)
    }
  }

  // Fallback se no tiver nenhuma selecionada
  useEffect(() => {
    if (companies.length > 0 && user && !activeCompanyId && !isSwitching) {
       // Apenas define localmente para evitar loop de RPC se o banco ainda no estiver migrado
       setActiveCompanyIdState(companies[0].id)
       localStorage.setItem(`activeCompanyId_${user.id}`, companies[0].id)
    }
  }, [companies, user, activeCompanyId, isSwitching])

  return (
    <CompanyContext.Provider value={{ activeCompanyId, setActiveCompanyId, companies, isLoading, isSwitching }}>
      {children}
    </CompanyContext.Provider>
  )
}

export const useCompany = () => useContext(CompanyContext)
